import { useCallback } from 'react'
import * as Sentry from '@sentry/browser'

import history from '@/routes/history'
import utils from '@/utils/utils'
import { isValidToken, logoutSession, setSession } from '@/utils/session'

import { AUTH_NOTICE } from '../constants/auth-notice'
import { AUTH_STEPS } from '../constants/auth-steps'
import { initialAuthState } from '../state/initial-state'
import { authApi } from '../services/auth-api'
import { redirectIfBannedUser } from '../services/auth-redirect'
import {
  buildLoginPath,
  buildLogoutLoginPath,
  getFullReturnPath,
} from '../utils/auth-return'
import { idleSubmitting } from '../utils/submitting'

export function useAuthSession ({
  user,
  setUser,
  state,
  setState,
  resetState,
  setIsAuth,
  clearPendingCredentials,
  destroyTurnstile,
}) {
  const token = utils.getToken()
  const isTokenOK = isValidToken(token)

  const refreshUserData = useCallback(async () => {
    try {
      const responseUserData = await authApi.fetchUser()
      setState({ userData: responseUserData })
    } catch (error) {
      Sentry.captureException(error)
    }
  }, [setState])

  const closeAuthNotice = useCallback(() => {
    setState({ isSessionExpired: false, authNotice: null })
  }, [setState])

  const logout = useCallback(async (redirectToLogin = true) => {
    try {
      await authApi.logout()
    } catch (error) {}

    clearPendingCredentials?.()
    logoutSession()
    setUser(null)
    setIsAuth(false)

    if (redirectToLogin) {
      resetState({
        ...initialAuthState,
        isAuthenticated: false,
        isInitialized: true,
        authPageStep: AUTH_STEPS.LOGIN_FORM,
        authNotice: AUTH_NOTICE.LOGOUT_SUCCESS,
        isSessionExpired: false,
        submittingState: idleSubmitting(),
      })
      history.replace(buildLogoutLoginPath())
      destroyTurnstile?.()
      return
    }

    setState({
      isAuthenticated: false,
      submittingState: idleSubmitting(),
      authPageStep: null,
      authNotice: null,
      isSessionExpired: false,
    })
    window.location.reload()
  }, [clearPendingCredentials, destroyTurnstile, resetState, setIsAuth, setState, setUser])

  const logoutExpired = useCallback(async () => {
    logoutSession()
    clearPendingCredentials?.()
    setUser(null)
    setIsAuth(false)
    resetState({
      ...initialAuthState,
      isSessionExpired: true,
      isAuthenticated: false,
      isInitialized: true,
      authPageStep: AUTH_STEPS.LOGIN_FORM,
      submittingState: idleSubmitting(),
    })
    history.replace(buildLoginPath(getFullReturnPath(window.location)))
    destroyTurnstile?.()
  }, [clearPendingCredentials, destroyTurnstile, resetState, setIsAuth, setUser])

  const redirectAfterSuccess = useCallback(() => {
    utils.removeDataNotLoggedUser()
    setState({ isFailedLogin: false, authNotice: null, isSessionExpired: false })
  }, [setState])

  const initialize = useCallback(async () => {
    if (!token) return
    if (!isTokenOK) return logoutExpired()
    if (!user) return
    if (redirectIfBannedUser(user)) return

    setUser(user)
    refreshUserData()
    setState({ isAuthenticated: true })
    setSession(token)
  }, [token, isTokenOK, user, logoutExpired, refreshUserData, setState, setUser])

  const markInitialized = useCallback(() => {
    setState({ isInitialized: true, closeSessionsStatus: true, fbData: null })
    setIsAuth(state.isAuthenticated)
  }, [setIsAuth, setState, state.isAuthenticated])

  return {
    token,
    isTokenOK,
    refreshUserData,
    closeAuthNotice,
    closeSessionExpired: closeAuthNotice,
    logout,
    logoutExpired,
    redirectAfterSuccess,
    initialize,
    markInitialized,
  }
}
